import React, { useState, useEffect } from "react";
import { Box, Typography, Grid, Paper } from "@mui/material";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import AxiosInstance from '../configs/axiosConfig';

const PRIMARY_COLOR = "#003595";

// Interface for the API response
interface PatientCountResponse {
    totalPatients: number;
    activePatients: number;
}

export default function StudyMetrics() {
    const [patientCounts, setPatientCounts] = useState<PatientCountResponse>({
        totalPatients: 0,
        activePatients: 0
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Calculate compliance percentage
    const compliancePercentage = patientCounts.totalPatients > 0 
        ? Math.round((patientCounts.activePatients / patientCounts.totalPatients) * 100)
        : 0;

    // Dynamic compliance data based on calculated percentage
    const complianceData = [
        { name: "Active", value: compliancePercentage, color: "#FF9F7A" },
        { name: "Inactive", value: 100 - compliancePercentage, color: "#E0E0E0" },
    ];

    // Fetch patient counts from API
    useEffect(() => {
        const fetchPatientCounts = async () => {
            try {
                setLoading(true);
                setError(null);
                const response = await AxiosInstance.get('/patients/count?call=webapp');
                setPatientCounts(response.data);
            } catch (err) {
                console.error('Error fetching patient counts:', err);
                setError('Failed to load patient counts');
            } finally {
                setLoading(false);
            }
        };

        fetchPatientCounts();
    }, []);
    return (
        <Box sx={{ mb: 4 }}>
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 3,
                }}
            >
                <Typography
                    variant="h6"
                    sx={{
                        color: "#333",
                        fontWeight: 500,
                        fontSize: "1.1rem",
                    }}
                >
                    Study Metrics
                </Typography>
                <Typography
                    variant="body2"
                    sx={{
                        color: "#666",
                        textTransform: "uppercase",
                        fontSize: "0.75rem",
                        letterSpacing: 1,
                        cursor: "pointer",
                        "&:hover": {
                            color: PRIMARY_COLOR,
                        },
                    }}
                >
                    VIEW ALL METRICS
                </Typography>
            </Box>

            <Grid container spacing={3}>
                {/* Compliance Chart */}
                <Grid item xs={12} md={4}>
                    <Paper
                        elevation={1}
                        sx={{
                            p: 3,
                            borderRadius: 2,
                            height: 200,
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                            alignItems: "center",
                            position: "relative",
                        }}
                    >
                        <Box sx={{ position: "relative", width: 100, height: 100 }}>
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie
                                        data={complianceData}
                                        cx="50%"
                                        cy="50%"
                                        innerRadius={25}
                                        outerRadius={50}
                                        startAngle={90}
                                        endAngle={-270}
                                        paddingAngle={0}
                                        dataKey="value"
                                    >
                                        {complianceData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={entry.color} />
                                        ))}
                                    </Pie>
                                </PieChart>
                            </ResponsiveContainer>
                            <Box
                                sx={{
                                    position: "absolute",
                                    top: "50%",
                                    left: "50%",
                                    transform: "translate(-50%, -50%)",
                                    textAlign: "center",
                                }}
                            >
                                <Typography
                                    variant="h4"
                                    sx={{
                                        color: "#333",
                                        fontWeight: 600,
                                        fontSize: "1.4rem",
                                    }}
                                >
                                    {loading ? "..." : error ? "!" : `${compliancePercentage}%`}
                                </Typography>
                            </Box>
                        </Box>
                        <Typography
                            variant="subtitle1"
                            sx={{
                                color: "#333",
                                fontWeight: 500,
                                mt: 1,
                                fontSize: "0.9rem",
                            }}
                        >
                            Compliance
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{
                                color: "#666",
                                fontSize: "0.8rem",
                            }}
                        >
                            Average
                        </Typography>
                        <Box
                            sx={{
                                position: "absolute",
                                top: 16,
                                right: 16,
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                            }}
                        >
                            {/* <Typography
                                variant="body2"
                                sx={{
                                    color: "#333",
                                    fontSize: "0.8rem",
                                    fontWeight: 500,
                                }}
                            >
                                70%
                            </Typography> */}
                            <Box
                                sx={{
                                    width: 16,
                                    height: 16,
                                    borderRadius: "50%",
                                    bgcolor: "#333",
                                    position: "relative",
                                }}
                            >
                                <Box
                                    sx={{
                                        position: "absolute",
                                        top: "50%",
                                        left: "50%",
                                        transform: "translate(-50%, -50%)",
                                        width: 8,
                                        height: 1,
                                        bgcolor: "#fff",
                                    }}
                                />
                            </Box>
                        </Box>
                    </Paper>
                </Grid>

                {/* Patients Enrolled */}
                <Grid item xs={12} md={4}>
                    <Paper
                        elevation={1}
                        sx={{
                            p: 3,
                            borderRadius: 2,
                            height: 200,
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                            alignItems: "center",
                        }}
                    >
                        <Typography
                            variant="h6"
                            sx={{
                                color: "#333",
                                fontWeight: 500,
                                mb: 2,
                                fontSize: "1rem",
                            }}
                        >
                            Patients enrolled
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{
                                color: "#666",
                                textTransform: "uppercase",
                                fontSize: "0.75rem",
                                letterSpacing: 1,
                                mb: 1,
                            }}
                        >
                            TOTAL
                        </Typography>
                        <Typography
                            variant="h2"
                            sx={{
                                color: "#333",
                                fontWeight: 600,
                                fontSize: "3.5rem",
                            }}
                        >
                            {loading ? "..." : error ? "!" : patientCounts.totalPatients}
                        </Typography>
                    </Paper>
                </Grid>

                {/* Active Patients */}
                <Grid item xs={12} md={4}>
                    <Paper
                        elevation={1}
                        sx={{
                            p: 3,
                            borderRadius: 2,
                            height: 200,
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                            alignItems: "center",
                        }}
                    >
                        <Typography
                            variant="h6"
                            sx={{
                                color: "#333",
                                fontWeight: 500,
                                mb: 2,
                                fontSize: "1rem",
                            }}
                        >
                            Active patients
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{
                                color: "#666",
                                textTransform: "uppercase",
                                fontSize: "0.75rem",
                                letterSpacing: 1,
                                mb: 1,
                            }}
                        >
                            TOTAL
                        </Typography>
                        <Typography
                            variant="h2"
                            sx={{
                                color: "#333",
                                fontWeight: 600,
                                fontSize: "3.5rem",
                            }}
                        >
                            {loading ? "..." : error ? "!" : patientCounts.activePatients}
                        </Typography>
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
}